import { useTranslation } from 'react-i18next'
import {
  Hammer,
  RotateCwSquare,
  Settings2Icon,
  TriangleAlert,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AssignmentViewMode,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentType,
  getEffectiveAssignmentViewMode,
  getEffectiveExpectedEntities,
  getAssignmentScopes,
  getAbilityReminderText,
  getAssignmentReminderText,
  isGeneralAssignmentType,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from '../../TeamPlans/components/assignmentTimeline'
import AssignmentScopeReminderText from '../../TeamPlans/components/AssignmentScopeReminderText'
import MarkerText from '../../TeamPlans/components/MarkerText'
import AssignmentExpectedEntitiesCount from '../../TeamPlans/components/AssignmentExpectedEntitiesCount'
import AssignmentEntityChip from '../../TeamPlans/components/AssignmentEntityChip'
import AssignmentEntityGrid from '../../TeamPlans/components/AssignmentEntityGrid'
import { getAssignmentEntityLabel } from '../../TeamPlans/components/assignmentEntityLabels'
import SpellIcon from '../../TeamPlans/components/SpellIcon'
import AssignmentTypeIcon from '../../TeamPlans/components/AssignmentTypeIcon'
import {
  AssignmentPhaseTimestampBadge,
  buildAssignmentPhaseTimestampBadgeData,
  PhaseDivider,
} from '../../TeamPlans/components/AssignmentPhaseTimestampBadge'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'
import { CUSTOM_ABILITY_SPELL_ID } from '../../TeamPlans/utils/encounterAssignmentRows'
import {
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
} from 'app/frontend/types/custom/RaidPlan'
import {
  ResolvedAssignmentEntity,
  ResolvedAssignmentParticipant,
} from './eventActivityTimelineResolver'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import Card from 'app/frontend/base/components/Card'
import EventPlanOverridePopover from './EventPlanOverridePopover'
import {
  assignedCharacterIdsForPicker,
  defaultAggregateOptions,
} from './EventPlanOverridePopover'
import { renderResolvedAssignmentEntityLabel } from './renderResolvedAssignmentEntityLabel'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import RichTextContent from 'app/frontend/base/components/RichTextContent'
import { hasNotesContent } from 'app/frontend/base/components/NotesEditor'
import { EncounterAssignmentEntityOverride } from './eventActivityPlanOverrides'
import {
  abilityVisualOverrideSelectionsAfterSelect,
  abilityVisualOverrideSelectionsFromSlot,
  buildAbilityVisualFillByScope,
  EventPlanOverrideSelection,
} from './abilityVisualFillOverrides'
import { useTeam } from 'app/frontend/base/config/context/team'
import { hasEventPlanAdminAccess } from 'app/frontend/base/config/permissions'
import TeamPlanAssignmentVisualCard from '../../TeamPlans/components/TeamPlanAssignmentVisualCard'
import useAbilityVisualPlanData from '../../TeamPlans/hooks/useAbilityVisualPlanData'
import {
  getAllSpecializations,
  getClass,
} from 'app/frontend/base/services/characterValues'
import { buildAssignmentPaletteEntities } from '../../TeamPlans/components/assignmentPaletteEntities'
import getAssignmentEntityIdentity from '../../TeamPlans/components/assignmentEntityIdentity'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { cn } from '@/lib/utils'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'

export type { EventPlanOverrideSelection }

type Props = {
  row: EncounterAbilityRow
  ability: RaidPlanEncounterAssignmentAbility
  owner: RaidPlanOwner
  phaseDividers: PhaseDivider[]
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>
  selectedParticipants: ResolvedAssignmentParticipant[]
  onOverrideSelect: (
    selection: EventPlanOverrideSelection | EventPlanOverrideSelection[],
  ) => void
  onResetAbilityOverrides: (abilityKey: string) => void
  onSetAbilityHidden: (abilityKey: string, hidden: boolean) => void
  isAbilityHidden?: boolean
  isReadonly?: boolean
}

const EventActivityPlanAbilityRow = ({
  row,
  ability,
  owner,
  phaseDividers,
  resolvedByScopeBoxKey,
  selectedParticipants,
  onOverrideSelect,
  onResetAbilityOverrides,
  onSetAbilityHidden,
  isAbilityHidden = false,
  isReadonly = false,
}: Props) => {
  const { t } = useTranslation()
  const { guild, team } = useTeam()
  const { accessLevel } = useAuth()
  const canEditPlans = hasEventPlanAdminAccess(accessLevel, guild, team.id)
  const {
    data: { user },
  } = usePageInfo()
  const abilityName = ability.title ?? row.name
  const isGeneralAssignment = isGeneralAssignmentType(
    getEffectiveAssignmentType(ability.assignment_type),
  )
  const abilityViewMode: AssignmentViewMode = isGeneralAssignment
    ? 'all_together'
    : getEffectiveAssignmentViewMode(ability.view_mode, row.entries.length)
  const abilityCustomGrouping = getEffectiveAbilityCustomGrouping(
    ability.spell_id,
    ability.custom_grouping,
  )
  const assignmentGroups = getEffectiveAssignmentGroups(
    row.assignmentGroups,
    ability.assignment_groups,
  )
  const scopes = getAssignmentScopes(
    row.entries,
    abilityViewMode,
    t,
    abilityCustomGrouping,
    ability.assignment_type,
    row.isCustom,
  )
  const getReminderForGroup = (
    scope: typeof scopes[number],
    boxIndex: number,
  ) =>
    getAssignmentReminderText({
      message: normalizeAssignmentBox(
        normalizeAssignmentCast(
          ability.casts?.[scope.castIndexes[0]],
          assignmentGroups,
        ).boxes[boxIndex],
      ).message,
      abilityMessage: ability.message,
      abilityLabel: abilityName,
      assignmentType: ability.assignment_type,
      isCustom: row.isCustom,
    })
  const getReminderForScope = (scope: typeof scopes[number]) =>
    assignmentGroups === 1 ? getReminderForGroup(scope, 0) : undefined
  const noteLabel = getAbilityReminderText(
    ability.message,
    abilityName,
    ability.assignment_type,
    row.isCustom,
  )
  const duplicateGeneralTitle =
    isGeneralAssignment &&
    noteLabel.trim().toLowerCase() === abilityName.trim().toLowerCase()
  const showGeneralScopeTitle = isGeneralAssignment && !duplicateGeneralTitle
  const getScopeBox = (scope: typeof scopes[number], boxIndex: number) =>
    normalizeAssignmentBox(
      normalizeAssignmentCast(
        ability.casts?.[scope.castIndexes[0]],
        assignmentGroups,
      ).boxes[boxIndex],
    )
  const renderExpectedEntitiesCount = (
    scope: typeof scopes[number],
    boxIndex: number,
  ) => {
    const box = getScopeBox(scope, boxIndex)
    const resolvedEntities =
      resolvedByScopeBoxKey[`${row.abilityKey}:${scope.id}:${boxIndex}`] ?? []
    const resolvedPlayerCount = resolvedEntities.filter(
      (entity) => entity.kind === 'participant',
    ).length
    const ignoredCount = resolvedEntities.filter(
      (entity) => entity.kind === 'ignored',
    ).length
    return (
      <AssignmentExpectedEntitiesCount
        actual={resolvedPlayerCount}
        expected={getEffectiveExpectedEntities(
          ability.expected_entities,
          box.expected_entities,
        )}
        mode='resolved'
        setupSize={selectedParticipants.length}
        ignored={ignoredCount}
      />
    )
  }
  const memberInfoByCharacterId = selectedParticipants.reduce<
    Record<number, { name: string; className: string }>
  >((acc, participant) => {
    acc[participant.characterId] = {
      name: participant.name,
      className: participant.characterClass,
    }
    return acc
  }, {})
  const specializationById = Object.entries(getAllSpecializations()).reduce<
    Record<
      number,
      { name: string; icon: string; classId: number; className: string }
    >
  >((acc, [specId, specialization]) => {
    const classId = specialization.class_id ?? 0
    acc[Number(specId)] = {
      name: specialization.name,
      icon: specialization.icon,
      classId,
      className: getClass(classId.toString()) ?? '',
    }
    return acc
  }, {})
  const { entitiesByScopeId, refsByScopeId, resolvedParticipantPlayers } =
    buildAbilityVisualFillByScope({
      scopes,
      assignmentGroups,
      abilityKey: row.abilityKey,
      ability,
      resolvedByScopeBoxKey,
    })
  const overriddenEntityIds = new Set(
    Object.values(refsByScopeId)
      .flat()
      .filter((ref) => ref.isOverridden && ref.entityId)
      .map((ref) => ref.entityId),
  )
  const dedupedRaidPlanPlayers = Array.from(
    new Map(
      resolvedParticipantPlayers.map((player) => [
        `${player.name}|${player.className}`,
        player,
      ]),
    ).values(),
  )
  const ownCharacterIds = new Set(
    (team.members ?? [])
      .filter((member) => member.characterReference.userId === user?.id)
      .map((member) => member.characterReference.id),
  )
  const classColor = (classId?: number) => {
    const className =
      classId === undefined ? undefined : getClass(classId.toString())
    return className
      ? `var(--color-class-${className.toLowerCase().replace(/\s+/g, '-')})`
      : 'var(--color-primary)'
  }
  const paletteLabelByIdentity = new Map(
    buildAssignmentPaletteEntities({
      t,
      members: team.members ?? [],
      specializationById,
      classColor,
    }).map((entry) => [getAssignmentEntityIdentity(entry.entity), entry.label]),
  )
  const getEntityLabel = (entity: RaidPlanAssignmentEntity) =>
    paletteLabelByIdentity.get(getAssignmentEntityIdentity(entity)) ??
    getAssignmentEntityLabel(t, entity)
  const { visualPlan, visualRaidPlan } = useAbilityVisualPlanData({
    owner,
    ability,
    scopes,
    entitiesByScopeId,
    entries: row.entries,
    phaseDividers,
    specializationById,
    memberInfoByCharacterId,
    raidPlanPlayers: dedupedRaidPlanPlayers,
    ownCharacterIds,
    overriddenEntityIds,
    getEntityLabel,
  })
  const canEditVisualEntities = !isReadonly && canEditPlans
  const handleVisualEntityOverrideSelect = (
    slotKey: string,
    override: EncounterAssignmentEntityOverride,
  ) => {
    if (!visualPlan) return
    const selections = abilityVisualOverrideSelectionsFromSlot({
      screens: visualPlan.screens,
      refsByScopeId,
      slotKey,
      override,
      abilityKey: row.abilityKey,
    })
    if (selections.length === 0) return
    onOverrideSelect(selections)
  }
  const selectedParticipantByCharacterId = (team.members ?? []).reduce<
    Record<number, wowaudit.TeamMembership>
  >((acc, member) => {
    acc[member.characterReference.id] = member
    return acc
  }, {})
  const participantClassByCharacterId = Object.fromEntries(
    Object.entries(selectedParticipantByCharacterId).map(
      ([characterId, member]) => [
        Number(characterId),
        member.characterReference.characterClass,
      ],
    ),
  )
  const aggregateOptions: Extract<
    EncounterAssignmentEntityOverride,
    { kind: 'aggregate' }
  >[] = defaultAggregateOptions.map((option) => ({
    kind: 'aggregate',
    entity: option.entity,
  }))
  const getIssueMessage = (entity: ResolvedAssignmentEntity) => {
    if (!entity.issue) return null
    if (entity.issue.type === 'insufficient_candidates') {
      const roleLabel =
        entity.issue.role === 'anyone'
          ? 'player'
          : entity.issue.role === 'non_tank'
          ? 'non-tank'
          : entity.issue.role === 'anyone_ranged'
          ? 'ranged player'
          : entity.issue.role === 'anyone_melee'
          ? 'melee player'
          : entity.issue.role
      const slotLabel =
        roleLabel ??
        (entity.sourceEntity.kind === 'by_class'
          ? getClass(entity.sourceEntity.class_id.toString()) ?? 'class'
          : 'player')
      return `No matching ${slotLabel} found for this slot.`
    }
    return `No match found for ${
      entity.issue.memberName ??
      getAssignmentEntityLabel(t, entity.sourceEntity)
    }.`
  }
  const getEntityChipLabel = (entity: ResolvedAssignmentEntity) => {
    return renderResolvedAssignmentEntityLabel({
      t,
      entity,
      participantClassByCharacterId,
    })
  }
  const abilityHideButton = isReadonly ? null : (
    <Button
      type='button'
      variant='outline'
      size='xs'
      onClick={() => {
        onSetAbilityHidden(row.abilityKey, !isAbilityHidden)
      }}
    >
      {isAbilityHidden ? 'Show' : 'Hide'}
    </Button>
  )
  const abilityActionsMenu = isReadonly ? null : (
    <ProtectedContent
      requirement='admin'
      section='events'
      renderUnauthorized={false}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='outline' size='xs'>
            <Settings2Icon className='h-3.5 w-3.5' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem
            onSelect={() => {
              onResetAbilityOverrides(row.abilityKey)
            }}
          >
            Reset changes
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              onSetAbilityHidden(row.abilityKey, !isAbilityHidden)
            }}
          >
            {isAbilityHidden ? 'Show' : 'Hide'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </ProtectedContent>
  )
  const abilityHeaderActions = (
    <div className='grow flex items-center justify-end gap-2 text-foreground'>
      {abilityHideButton}
      {abilityActionsMenu}
    </div>
  )
  const isCustomAbility = row.isCustom

  if (isAbilityHidden) {
    const hiddenCard = (
      <Card className='mb-3 py-3'>
        <div className='flex items-center gap-2 text-sm font-medium'>
          <AssignmentTypeIcon assignmentType={ability.assignment_type} />
          <span className='truncate'>{abilityName}</span>
          <Badge variant='outline'>Hidden</Badge>
          {abilityHeaderActions}
        </div>
      </Card>
    )
    if (isReadonly) {
      return (
        <ProtectedContent
          requirement='admin'
          section='events'
          renderUnauthorized={false}
        >
          {hiddenCard}
        </ProtectedContent>
      )
    }
    return hiddenCard
  }

  return (
    <Card className='mb-3 p-0'>
      <div className='divide-y divide-border'>
        <div className='px-4 py-3'>
          <div
            className='flex items-center gap-2 text-sm font-medium flex-wrap'
            style={{ color: row.color }}
          >
            <div
              className='flex items-center gap-2 min-w-0'
              style={{ color: row.color }}
            >
              <SpellIcon
                spellId={
                  isCustomAbility ? CUSTOM_ABILITY_SPELL_ID : row.spellId
                }
                className='w-6 h-6 rounded border border-border'
              />
              <AssignmentTypeIcon assignmentType={ability.assignment_type} />
              {isCustomAbility ? (
                <span className='truncate'>{abilityName}</span>
              ) : (
                <a
                  href={`https://wowhead.com/spell=${row.spellId}`}
                  target='_blank'
                  rel='noreferrer'
                  className='truncate'
                  style={{ color: row.color }}
                >
                  {abilityName}
                </a>
              )}
            </div>
            {scopes.length === 1 && !isGeneralAssignment ? (
              <div className='flex flex-wrap items-center gap-1 ml-1 text-xs text-muted-foreground'>
                {assignmentGroups === 1
                  ? renderExpectedEntitiesCount(scopes[0], 0)
                  : null}
                {scopes[0].castGroups.map((castGroup, castGroupIndex) => (
                  <AssignmentPhaseTimestampBadge
                    key={`${scopes[0].id}:${castGroupIndex}`}
                    {...buildAssignmentPhaseTimestampBadgeData({
                      castIndexes: castGroup,
                      entries: row.entries,
                      phaseDividers,
                    })}
                  />
                ))}
                <AssignmentScopeReminderText
                  text={getReminderForScope(scopes[0])}
                />
              </div>
            ) : null}

            {abilityHeaderActions}
          </div>
          {hasNotesContent(ability.description) ? (
            <div className='mt-2 text-foreground'>
              <RichTextContent content={ability.description ?? ''} />
            </div>
          ) : null}
        </div>
        {scopes.map((scope) => {
          const representativeCast = normalizeAssignmentCast(
            ability.casts?.[scope.castIndexes[0]],
            assignmentGroups,
          )
          const scopeRefs = refsByScopeId[scope.id] ?? []
          const castIds = scope.castIndexes
            .map(
              (castIndex) =>
                normalizeAssignmentCast(
                  ability.casts?.[castIndex],
                  assignmentGroups,
                ).id,
            )
            .filter((castId): castId is string => Boolean(castId))
          const groupSlotCounts = Array.from(
            { length: assignmentGroups },
            (_, boxIndex) =>
              (
                resolvedByScopeBoxKey[
                  `${row.abilityKey}:${scope.id}:${boxIndex}`
                ] ?? []
              ).length,
          )
          const groupStartOffsets = groupSlotCounts.reduce<number[]>(
            (acc, _count, index) => {
              acc[index] =
                index === 0 ? 0 : acc[index - 1] + groupSlotCounts[index - 1]
              return acc
            },
            [],
          )
          return (
            <div key={scope.id} className='px-4 py-3'>
              {(scopes.length > 1 || isGeneralAssignment) &&
              (!isGeneralAssignment ||
                showGeneralScopeTitle ||
                assignmentGroups === 1) ? (
                <div className='flex flex-wrap items-center gap-3 mb-1'>
                  <div className='flex items-center gap-2'>
                    {(!isGeneralAssignment || showGeneralScopeTitle) &&
                    (isGeneralAssignment ? noteLabel : scope.label) ? (
                      <div className='font-medium text-sm'>
                        <MarkerText
                          text={isGeneralAssignment ? noteLabel : scope.label}
                        />
                      </div>
                    ) : null}
                    {assignmentGroups === 1
                      ? renderExpectedEntitiesCount(scope, 0)
                      : null}
                  </div>
                  {isGeneralAssignment ? null : (
                    <div className='flex flex-wrap items-center gap-1 text-xs text-muted-foreground'>
                      {scope.castGroups.map((castGroup, castGroupIndex) => (
                        <AssignmentPhaseTimestampBadge
                          key={`${scope.id}:${castGroupIndex}`}
                          {...buildAssignmentPhaseTimestampBadgeData({
                            castIndexes: castGroup,
                            entries: row.entries,
                            phaseDividers,
                          })}
                        />
                      ))}
                      <AssignmentScopeReminderText
                        text={getReminderForScope(scope)}
                      />
                    </div>
                  )}
                </div>
              ) : null}

              {hasNotesContent(representativeCast.description) ? (
                <div className='mb-2 text-foreground'>
                  <RichTextContent
                    content={representativeCast.description ?? ''}
                  />
                </div>
              ) : null}

              <div
                className='grid min-w-0 gap-2'
                style={{
                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(min(100%, 12rem), 1fr))',
                }}
              >
                {Array.from({ length: assignmentGroups }, (_, boxIndex) => {
                  const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
                  const resolvedEntities = resolvedByScopeBoxKey[key] ?? []
                  const box = normalizeAssignmentBox(
                    representativeCast.boxes[boxIndex],
                  )
                  const groupLabelText = t(
                    'dashboard.team.plans.assignments.group-label',
                    { count: boxIndex + 1 },
                  )
                  const generalGroupLabel =
                    getScopeBox(scope, boxIndex).message?.trim() ||
                    groupLabelText
                  return (
                    <div key={boxIndex} className='min-w-0 pt-1'>
                      {assignmentGroups > 1 ? (
                        <div className='text-xs border-b mb-1 pb-1 flex items-center gap-2 flex-wrap'>
                          <span
                            className={
                              isGeneralAssignment ? 'text-sm' : undefined
                            }
                          >
                            {isGeneralAssignment ? (
                              <MarkerText text={generalGroupLabel} />
                            ) : (
                              groupLabelText
                            )}
                          </span>
                          <AssignmentExpectedEntitiesCount
                            actual={
                              resolvedEntities.filter(
                                (entity) => entity.kind === 'participant',
                              ).length
                            }
                            expected={getEffectiveExpectedEntities(
                              ability.expected_entities,
                              box.expected_entities,
                            )}
                            mode='resolved'
                            setupSize={selectedParticipants.length}
                            ignored={
                              resolvedEntities.filter(
                                (entity) => entity.kind === 'ignored',
                              ).length
                            }
                          />
                          {isGeneralAssignment ? null : (
                            <AssignmentScopeReminderText
                              text={getReminderForGroup(scope, boxIndex)}
                            />
                          )}
                        </div>
                      ) : null}
                      {resolvedEntities.length === 0 ? (
                        <div className='text-xs text-muted-foreground'>
                          No resolved assignments
                        </div>
                      ) : (
                        <AssignmentEntityGrid>
                          {resolvedEntities.map((entity, entityIndex) => {
                            const issueMessage = getIssueMessage(entity)
                            const sourceEntity =
                              box.entities[entityIndex] ?? entity.sourceEntity
                            const boxId = box.id
                            const entityId = sourceEntity?.id
                            const hasOverride = entity.isOverridden === true
                            const hasMemberFallbackMatch =
                              sourceEntity?.kind === 'member' &&
                              entity.kind === 'participant' &&
                              entity.participant.characterId !==
                                sourceEntity.character_id
                            const sourceMemberParticipant =
                              sourceEntity?.kind === 'member'
                                ? selectedParticipantByCharacterId[
                                    sourceEntity.character_id
                                  ]
                                : undefined
                            const baseKey = `${
                              sourceEntity?.id ?? entityIndex
                            }-${entityIndex}`
                            const isParticipant = entity.kind === 'participant'
                            const isIgnored = entity.kind === 'ignored'
                            const isOwnCharacterEntity =
                              isParticipant &&
                              selectedParticipantByCharacterId[
                                entity.participant.characterId
                              ]?.characterReference.userId === user?.id
                            const chip = (
                              <AssignmentEntityChip
                                index={
                                  (groupStartOffsets[boxIndex] ?? 0) +
                                  entityIndex
                                }
                                className={cn(
                                  'w-full',
                                  isOwnCharacterEntity &&
                                    'border-subscription-gold',
                                  isIgnored
                                    ? 'text-muted-foreground'
                                    : issueMessage
                                    ? isParticipant
                                      ? 'border-warning'
                                      : 'border-warning-foreground bg-warning hover:bg-warning'
                                    : undefined,
                                )}
                                endSlot={
                                  issueMessage ||
                                  hasOverride ||
                                  hasMemberFallbackMatch ? (
                                    <span className='inline-flex items-center gap-0.5'>
                                      {hasOverride ? (
                                        <Hammer className='h-4 w-4 text-info-foreground' />
                                      ) : issueMessage ? (
                                        <TriangleAlert className='h-4 w-4 text-warning-foreground' />
                                      ) : hasMemberFallbackMatch ? (
                                        <RotateCwSquare className='h-4 w-4 text-info-foreground' />
                                      ) : null}
                                    </span>
                                  ) : undefined
                                }
                                label={getEntityChipLabel(entity)}
                              />
                            )
                            const chipWithIssue =
                              !issueMessage &&
                              !hasOverride &&
                              !hasMemberFallbackMatch ? (
                                chip
                              ) : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className='block min-w-0 w-full'>
                                      {chip}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {hasOverride ? (
                                      'Manually edited'
                                    ) : hasMemberFallbackMatch ? (
                                      <>
                                        Replaces{' '}
                                        <span
                                          className={
                                            sourceMemberParticipant
                                              ? classColours[
                                                  sourceMemberParticipant
                                                    .characterReference
                                                    .characterClass
                                                ].text
                                              : undefined
                                          }
                                        >
                                          {sourceEntity.label}
                                        </span>{' '}
                                        in the plan
                                      </>
                                    ) : (
                                      issueMessage
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              )
                            if (
                              isReadonly ||
                              !entityId ||
                              !boxId ||
                              !castIds.length
                            ) {
                              return (
                                <span key={baseKey} className='min-w-0 w-full'>
                                  {chipWithIssue}
                                </span>
                              )
                            }
                            const selectedOverride:
                              | EncounterAssignmentEntityOverride
                              | undefined = isParticipant
                              ? {
                                  kind: 'participant',
                                  characterId: entity.participant.characterId,
                                }
                              : isIgnored
                              ? { kind: 'ignored' }
                              : entity.kind === 'aggregate' &&
                                (entity.sourceEntity.kind === 'everyone' ||
                                  entity.sourceEntity.kind === 'collection' ||
                                  entity.sourceEntity.kind === 'group')
                              ? {
                                  kind: 'aggregate',
                                  entity: entity.sourceEntity,
                                }
                              : undefined
                            const clickedIndex = scopeRefs.findIndex(
                              (ref) =>
                                ref.boxId === boxId &&
                                ref.entityId === entityId,
                            )
                            return (
                              <div key={baseKey} className='min-w-0 w-full'>
                                <EventPlanOverridePopover
                                  trigger={chipWithIssue}
                                  disabled={!canEditVisualEntities}
                                  participants={selectedParticipants}
                                  assignedCharacterIds={assignedCharacterIdsForPicker(
                                    scopeRefs.map((ref) => ref.characterId),
                                    selectedOverride?.kind === 'participant'
                                      ? selectedOverride.characterId
                                      : undefined,
                                  )}
                                  aggregateOptions={aggregateOptions}
                                  selectedOverride={selectedOverride}
                                  onSelect={(override) => {
                                    if (clickedIndex < 0) {
                                      onOverrideSelect({
                                        abilityKey: row.abilityKey,
                                        castIds,
                                        boxId,
                                        entityId,
                                        override,
                                      })
                                      return
                                    }
                                    const selections =
                                      abilityVisualOverrideSelectionsAfterSelect(
                                        {
                                          refs: scopeRefs,
                                          clickedIndex,
                                          override,
                                          abilityKey: row.abilityKey,
                                        },
                                      )
                                    if (selections.length === 0) return
                                    onOverrideSelect(selections)
                                  }}
                                />
                              </div>
                            )
                          })}
                        </AssignmentEntityGrid>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
        {visualPlan || visualRaidPlan ? (
          <div>
            <TeamPlanAssignmentVisualCard
              label={t('dashboard.team.plans.assignments.visual-section')}
              visualPlan={visualPlan}
              visual={visualRaidPlan}
              defaultExpanded
              asCard={false}
              canEditEntities={canEditVisualEntities}
              canEditUnassignedSlots={false}
              selectedParticipants={
                canEditVisualEntities ? selectedParticipants : undefined
              }
              onEntityOverrideSelect={
                canEditVisualEntities
                  ? handleVisualEntityOverrideSelect
                  : undefined
              }
              onResetOverrides={
                canEditVisualEntities
                  ? () => onResetAbilityOverrides(row.abilityKey)
                  : undefined
              }
            />
          </div>
        ) : null}
      </div>
    </Card>
  )
}

export default EventActivityPlanAbilityRow
